import { apiRequest } from '../../api/client';

export type EmailSecurity = 'NONE' | 'STARTTLS' | 'SSL_TLS';
export type EffectiveEmailSource = 'app' | 'env' | 'none';

/** Matches `apps/api` `EmailSettingsView` (`get-email-settings.use-case.ts`). The password never travels back. */
export interface EmailSettings {
  configured: boolean;
  enabled: boolean;
  host: string | null;
  port: number | null;
  security: EmailSecurity | null;
  username: string | null;
  hasPassword: boolean;
  passwordReadable: boolean;
  fromName: string | null;
  fromAddress: string | null;
  replyTo: string | null;
  effectiveSource: EffectiveEmailSource;
  envFallbackAvailable: boolean;
  pendingEmailCount: number;
  updatedAt: string | null;
  updatedBy: { id: string; fullName: string } | null;
}

/**
 * Matches `apps/api` `UpdateEmailSettingsDto`. `password` is omitted to keep the
 * stored one; `clearPassword` deletes it; the two are mutually exclusive.
 */
export interface UpdateEmailSettingsInput {
  enabled: boolean;
  host: string;
  port: number;
  security: EmailSecurity;
  username: string | null;
  password?: string;
  clearPassword?: boolean;
  fromName: string | null;
  fromAddress: string;
  replyTo: string | null;
}

/** Matches `apps/api` `TestEmailResult` (`send-test-email.use-case.ts`). */
export type TestEmailResult =
  | { ok: true; messageId: string; source: 'app' | 'env' }
  | { ok: false; error: { code: string; message: string; responseCode?: number } };

/** Matches `apps/api` `GET /email-settings`. */
export function getEmailSettings(): Promise<EmailSettings> {
  return apiRequest<EmailSettings>('/email-settings');
}

/** Matches `apps/api` `PUT /email-settings`. */
export function updateEmailSettings(input: UpdateEmailSettingsInput): Promise<EmailSettings> {
  return apiRequest<EmailSettings>('/email-settings', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `POST /email-settings/test`: tests the saved configuration, not the form. */
export function sendTestEmail(to: string): Promise<TestEmailResult> {
  return apiRequest<TestEmailResult>('/email-settings/test', {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}
